import React from 'react';

import { Size } from '../../types';
import { useFieldLayout } from './field-shell';
import { RequiredMark } from './required-mark';

export interface FieldCaptionProps {
  /** The caption's text. */
  children: React.ReactNode;
  /** The step of the fields it sits among, as a field label's. */
  size?: Size;
  /** Marks the caption with an asterisk, read aloud: no control beside it carries `aria-required`. */
  isRequired?: boolean;
  /** For an element that points at the caption (`aria-labelledby` on a group it names). */
  id?: string;
}

/**
 * A field label's type for a name that labels no control: the part of a card
 * it heads ("Answer" over a question's answer), or a list the reader cannot
 * change (a read-only field's files). It reads as the labels of the fields
 * around it, at the step and label position they take, and it is text, never
 * a `<label>`, which must point at a control (field.md, "Captions").
 */
export const FieldCaption = React.forwardRef<HTMLSpanElement, FieldCaptionProps>(
  function FieldCaption({ children, size = 'md', isRequired = false, id }, ref) {
    const { styles } = useFieldLayout(size);
    return (
      <span ref={ref} id={id} className={styles.label()} data-component="field-caption">
        {children}
        {isRequired && <RequiredMark announces />}
      </span>
    );
  },
);
